import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { 
  FileText, 
  Save, 
  Download, 
  ChevronLeft, 
  ChevronRight,
  Briefcase,
  User,
  PenTool,
  PenLine,
  CheckCircle,
  Loader2,
  AlertCircle,
  Star,
  RefreshCw,
  Eye,
  Printer,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { coverLetterAPI, resumeAPI, profileAPI, resolveAssetUrl, introsAPI, successAgreementAPI } from "../../services/api";
import ProfileSyncModal, { fieldLabelFor } from "./ProfileSyncModal";
import SignatureModal from "./SignatureModal";
import useAutosave from "../../hooks/useAutosave";
import AutosaveIndicator from "./AutosaveIndicator";
import FieldLengthHint from "./FieldLengthHint";

// Iter-26 Phase 1: Cover-letter identity fields that cascade from the
// master profile. Editing any of these triggers the 3-way sync modal
// on Save. Keep aligned with PROFILE_CASCADING in backend/server.py
// (CL doesn't carry target_role explicitly so we omit it here).
const CASCADING_KEYS = ["full_name", "location", "linkedin", "website"];

const CoverLetterGenerator = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [coverLetterId, setCoverLetterId] = useState(null);
  // Iter-36: list of past Application Packages this candidate has downloaded.
  // Each row exposes a "Re-print" button which streams back the exact same
  // PDF bytes (and therefore the exact same locked QR) the user originally
  // downloaded — so duplicate hard copies all open the same package URL.
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [reprintingId, setReprintingId] = useState(null);
  // Iter-37.6: optional Success Agreement add-on for the Application
  // Package. OFF by default — the standard package is CL + Resume +
  // QR-linked intro. We only surface the toggle if the candidate has
  // an active agreement to include.
  const [hasActiveAgreement, setHasActiveAgreement] = useState(false);
  const [includeSuccessAgreement, setIncludeSuccessAgreement] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const s = await successAgreementAPI.getState();
        setHasActiveAgreement(!!s?.active);
      } catch (_) {
        setHasActiveAgreement(false);
      }
    })();
  }, []);

  const loadPackages = useCallback(async () => {
    try {
      setPackagesLoading(true);
      const rows = await introsAPI.listPackages();
      setPackages(rows || []);
    } catch (e) {
      console.warn("loadPackages failed", e);
    } finally {
      setPackagesLoading(false);
    }
  }, []);

  useEffect(() => { loadPackages(); }, [loadPackages]);

  const handleReprintPackage = async (pkg) => {
    setReprintingId(pkg.id);
    try {
      const blob = await introsAPI.reprintPackage(pkg.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const tag = (pkg.cover_letter_title || pkg.id.slice(0, 8)).replace(/\s+/g, "-").toLowerCase();
      a.download = `application-package-${tag}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Reprinted with the original QR.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reprint that package.");
    } finally {
      setReprintingId(null);
    }
  };

  const [formData, setFormData] = useState({
    // Step 1: Job Details
    job_title: "",
    company_name: "",
    recipient_name: "",
    job_description: "",
    
    // Step 2: Personal Info (auto-populated from resume)
    full_name: "",
    email: "",
    phone: "",
    location: "",
    linkedin: "",
    website: "",
    
    // Step 3: Content & Tone
    content: "",
    tone: "Professional",
    title: "My Cover Letter",

    // Key Strengths (for PDF)
    key_strengths: [
      { title: "", description: "" },
      { title: "", description: "" },
      { title: "", description: "" }
    ]
  });

  // PDF Preview state
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewTimerRef = useRef(null);
  const prevBlobUrlRef = useRef(null);

  // Iter-26 Phase 1: master Profile snapshot + 3-way sync modal state.
  const [profile, setProfile] = useState(null);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [pendingChangedFields, setPendingChangedFields] = useState([]);
  const [pendingAction, setPendingAction] = useState(null); // 'save' | 'download' | 'package'
  const touchedFieldsRef = useRef(new Set());

  // Iter-28: surface the canonical (Profile-stored) signature in the
  // final cover-letter step. Edits there route through the same
  // SignatureModal — Profile remains the canonical store.
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureUrl, setSignatureUrl] = useState("");

  const steps = [
    { number: 1, title: "Job Details", icon: Briefcase, description: "Position and company information" },
    { number: 2, title: "Personal Info", icon: User, description: "Your contact information" },
    { number: 3, title: "Content & Tone", icon: PenTool, description: "Write your cover letter" }
  ];

  const toneOptions = ["Professional", "Friendly", "Enthusiastic", "Confident"];

  // Load existing cover letter on component mount
  useEffect(() => {
    loadCoverLetter();
    loadResumeData();
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const p = await profileAPI.getProfile();
      setProfile(p);
      setSignatureUrl(p?.signature_url || "");
    } catch (err) {
      console.warn("Could not load profile:", err);
    }
  };

  // Iter-26 Phase 1: smart cascade — fill blank CASCADING fields and
  // overwrite identity-LOCKED fields (email, phone) from the master
  // profile so the cover letter form mirrors what will render on the
  // PDF.
  useEffect(() => {
    if (!profile) return;
    setFormData((prev) => {
      const next = { ...prev };
      CASCADING_KEYS.forEach((k) => {
        if (!next[k] && profile[k]) next[k] = profile[k];
      });
      if (!next.full_name && profile.full_name) next.full_name = profile.full_name;
      if (profile.email) next.email = profile.email;
      if (profile.phone) next.phone = profile.phone;
      return next;
    });
  }, [profile]);

  const computeChangedCascadingFields = useCallback(() => {
    if (!profile) return [];
    const out = [];
    CASCADING_KEYS.forEach((k) => {
      if (!touchedFieldsRef.current.has(k)) return;
      const docVal = (formData[k] || "").toString().trim();
      const profVal = (profile[k] || "").toString().trim();
      if (docVal !== profVal && docVal) {
        out.push({
          key: k,
          label: fieldLabelFor(k),
          current: docVal,
          previous: profVal,
        });
      }
    });
    return out;
  }, [profile, formData]);

  const loadCoverLetter = async () => {
    try {
      setLoading(true);
      const coverLetter = await coverLetterAPI.getCoverLetter();
      if (coverLetter) {
        // Preserve default key_strengths if not present in loaded data
        setFormData(prev => ({
          ...prev,
          ...coverLetter,
          job_description: coverLetter.job_description || "",
          content: coverLetter.content || "",
          key_strengths: coverLetter.key_strengths && coverLetter.key_strengths.length > 0 
            ? coverLetter.key_strengths 
            : prev.key_strengths
        }));
        setCoverLetterId(coverLetter.id);
        toast.success("Cover letter loaded successfully!");
      }
    } catch (error) {
      // No existing cover letter, that's fine
      console.log("No existing cover letter found");
    } finally {
      setLoading(false);
    }
  };

  const loadResumeData = async () => {
    try {
      const resume = await resumeAPI.getResume();
      if (resume && resume.personal_info) {
        const personalInfo = resume.personal_info;
        setFormData(prev => ({
          ...prev,
          full_name: prev.full_name || personalInfo.full_name || "",
          email: prev.email || personalInfo.email || "",
          phone: prev.phone || personalInfo.phone || "",
          location: prev.location || personalInfo.location || "",
          linkedin: prev.linkedin || personalInfo.linkedin || "",
          website: prev.website || personalInfo.website || ""
        }));
      }
    } catch (error) {
      console.log("No resume data found to auto-populate");
    }
  };

  // Generate PDF preview
  const generatePreview = useCallback(async () => {
    // Need at least some content or personal info to generate a meaningful preview
    if (!formData.full_name && !formData.content && !formData.company_name) {
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const pdfData = {
        full_name: formData.full_name || "",
        email: formData.email || "",
        phone: formData.phone || "",
        location: formData.location || "",
        professional_title: formData.job_title || "",
        recipient_name: formData.recipient_name || "",
        company_name: formData.company_name || "",
        greeting: formData.recipient_name ? `Dear ${formData.recipient_name},` : "Dear Hiring Manager,",
        content: formData.content || "",
        key_strengths: (formData.key_strengths || []).filter(s => s.title || s.description),
      };

      const blob = await coverLetterAPI.generatePDF(pdfData);
      const url = window.URL.createObjectURL(blob);

      // Revoke previous blob URL to prevent memory leaks
      if (prevBlobUrlRef.current) {
        window.URL.revokeObjectURL(prevBlobUrlRef.current);
      }
      prevBlobUrlRef.current = url;
      setPdfBlobUrl(url);
    } catch (err) {
      console.error("Preview generation failed:", err);
      setPreviewError("Could not generate preview");
    } finally {
      setPreviewLoading(false);
    }
  }, [formData]);

  // Debounced preview update on form data changes
  useEffect(() => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
    }
    previewTimerRef.current = setTimeout(() => {
      generatePreview();
    }, 1500);

    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }
    };
  }, [generatePreview]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (prevBlobUrlRef.current) {
        window.URL.revokeObjectURL(prevBlobUrlRef.current);
      }
    };
  }, []);

  const handleInputChange = (field, value) => {
    // Iter-26 Phase 1: track which cascading fields the user touched
    // so the sync-modal logic can distinguish overrides from pristine
    // profile values.
    if (CASCADING_KEYS.includes(field)) {
      touchedFieldsRef.current.add(field);
    }
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Iter-39 autosave: useAutosave hook (below) watches formData and
    // persists with a 1.5s debounce — no manual timer needed here.
  };

  const handleStrengthChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...(prev.key_strengths || [{ title: "", description: "" }, { title: "", description: "" }, { title: "", description: "" }])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, key_strengths: updated };
    });
  };

  // Actual persist helper extracted so the modal callbacks + the
  // autosave hook can share the save path without duplicating code.
  const persistCoverLetter = useCallback(async (data) => {
    const payload = data || formData;
    if (coverLetterId) {
      await coverLetterAPI.updateCoverLetter(payload);
    } else {
      const response = await coverLetterAPI.createCoverLetter(payload);
      setCoverLetterId(response.id);
    }
  }, [coverLetterId, formData]);

  // Iter-39 — debounced autosave. Only kicks in once the candidate has
  // typed something meaningful so brand-new users don't get spammed
  // with empty-document POSTs.
  const autosaveEnabled = !loading;
  const { status: autosaveStatus, lastSavedAt: autosaveAt, flush: autosaveFlush } = useAutosave(
    formData,
    persistCoverLetter,
    {
      delay: 1500,
      enabled: autosaveEnabled,
      shouldSave: (d) => !!(d.job_title || d.company_name || d.content || d.full_name),
    },
  );

  const handleSave = async () => {
    const changed = computeChangedCascadingFields();
    if (changed.length > 0) {
      setPendingChangedFields(changed);
      setPendingAction("save");
      setSyncModalOpen(true);
      return;
    }
    try {
      setSaving(true);
      await persistCoverLetter();
      toast.success("Cover letter saved");
    } catch (error) {
      toast.error(error.message || "Failed to save cover letter");
    } finally {
      setSaving(false);
    }
  };

  // Modal callback: save cover letter only — profile untouched.
  const handleSyncDocOnly = async () => {
    setSyncModalOpen(false);
    setSaving(true);
    try {
      await persistCoverLetter();
      toast.success("Cover letter saved");
    } catch (e) {
      toast.error("Could not save cover letter — please try again.");
    } finally {
      setPendingChangedFields([]);
      setPendingAction(null);
      touchedFieldsRef.current = new Set();
      setSaving(false);
    }
  };

  // Modal callback: save cover letter + patch the profile so the new
  // cascading values become the master copy.
  const handleSyncAll = async () => {
    setSyncModalOpen(false);
    setSaving(true);
    const patch = {};
    pendingChangedFields.forEach((f) => { patch[f.key] = f.current; });
    try {
      await persistCoverLetter();
      const updated = await profileAPI.syncFromDocument(patch);
      setProfile(updated);
      toast.success("Cover letter + profile updated. Changes will apply to all your documents.");
    } catch (err) {
      console.error("Sync to profile failed:", err);
      toast.error("Saved this cover letter, but couldn't update your profile.");
    } finally {
      setPendingChangedFields([]);
      setPendingAction(null);
      touchedFieldsRef.current = new Set();
      setSaving(false);
    }
  };

  const handleSyncCancel = () => {
    setSyncModalOpen(false);
    setPendingChangedFields([]);
    setPendingAction(null);
  };

  const handleDownload = async () => {
    if (!formData.content) {
      toast.error("No content to download. Please write your cover letter first.");
      return;
    }

    try {
      setSaving(true);
      toast.info("Generating PDF...");

      const pdfData = {
        full_name: formData.full_name || "",
        email: formData.email || "",
        phone: formData.phone || "",
        location: formData.location || "",
        professional_title: formData.job_title || "",
        recipient_name: formData.recipient_name || "",
        company_name: formData.company_name || "",
        greeting: formData.recipient_name ? `Dear ${formData.recipient_name},` : "Dear Hiring Manager,",
        content: formData.content,
        key_strengths: (formData.key_strengths || []).filter(s => s.title || s.description),
      };

      const blob = await coverLetterAPI.generatePDF(pdfData);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `cover-letter-${formData.company_name?.replace(/\s+/g, '-').toLowerCase() || 'letter'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Cover letter PDF downloaded!");
    } catch (error) {
      console.error("PDF generation failed:", error);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Iter-24: Download cover letter (page 1) + saved resume (page 2+) as
  // a single Application Package PDF the candidate can print and hand
  // out to recruiters. Uses the live cover-letter form data + the
  // Iter-25: pre-download guard rail. The Application Package merges the
  // candidate's current cover-letter form data (page 1) with their saved
  // resume from the DB (page 2+). If the candidate's name in the cover
  // letter doesn't match the name on the saved resume, the merged PDF
  // would silently show two different identities — exactly the
  // "Hunter Cromwell cover letter + John Michael Doe resume" failure
  // mode reported by the user. We fetch the saved resume first, compare
  // names, and require explicit confirmation before merging.
  const _normalizeName = (s) =>
    (s || "").trim().replace(/\s+/g, " ").toLowerCase();

  const checkIdentityMismatch = async () => {
    try {
      const saved = await resumeAPI.getResume();
      if (!saved) return null;
      const savedName = _normalizeName(
        (saved.personal_info || {}).full_name,
      );
      const formName = _normalizeName(formData.full_name);
      if (!savedName || !formName) return null;
      if (savedName === formName) return null;
      return {
        savedName: (saved.personal_info || {}).full_name,
        formName: formData.full_name,
      };
    } catch (e) {
      console.warn("identity check skipped:", e);
      return null;
    }
  };

  // candidate's saved resume.
  const handleDownloadPackage = async () => {
    if (!formData.content) {
      toast.error("No cover letter content to package.");
      return;
    }
    const mismatch = await checkIdentityMismatch();
    if (mismatch) {
      // Use the browser's confirm so the candidate sees a hard stop —
      // a soft toast is too easy to miss on a final-step download.
      const proceed = window.confirm(
        `Heads up — your saved resume's name is "${mismatch.savedName}" ` +
        `but your cover letter is for "${mismatch.formName}". ` +
        `The Application Package will merge both pages as-is.\n\n` +
        `Click OK to download anyway, or Cancel to update your resume ` +
        `in the Resume Builder first.`
      );
      if (!proceed) {
        toast.info("Open Resume Builder to update your resume identity.");
        return;
      }
    }
    try {
      setSaving(true);
      toast.info("Building your application package…");
      // Iter-36: the locked /pdf endpoint reads the saved cover-letter
      // from the DB — auto-save any in-memory changes first so the
      // generated PDF reflects what the candidate sees on screen.
      try {
        await persistCoverLetter();
      } catch (saveErr) {
        console.warn("auto-save before package download failed", saveErr);
        // Still try to download — the user might just have nothing new to save.
      }
      // Iter-36: this is the LOCKED path — calls /api/application-package/pdf
      // which (1) creates a new package row, (2) embeds /p/{package_id} in
      // the QR, (3) persists the PDF bytes so re-printing this same package
      // later returns identical content.
      const blob = await coverLetterAPI.downloadApplicationPackage({ includeSuccessAgreement });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeCompany = (formData.company_name || "letter").replace(/\s+/g, "-").toLowerCase();
      link.download = `application-package-${safeCompany}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Application Package downloaded — new package created.");
      // Refresh the past-packages list below.
      await loadPackages();
    } catch (error) {
      console.error("Package generation failed:", error);
      const detail = error?.response?.data;
      // The backend returns JSON detail wrapped in a Blob (because we
      // requested a blob response type), so we have to re-parse it.
      if (detail instanceof Blob) {
        try {
          const txt = await detail.text();
          const parsed = JSON.parse(txt);
          toast.error(parsed.detail || "Could not build application package.");
        } catch {
          toast.error("Could not build application package.");
        }
      } else {
        toast.error("Could not build application package. Save your resume first.");
      }
    } finally {
      setSaving(false);
    }
  };

  const nextStep = async () => {
    if (currentStep < 3) {
      // Iter-39 autosave: flush any pending edits before navigating so
      // the user never loses recent typing on step transitions.
      try { await autosaveFlush(); } catch (_) { /* non-fatal */ }
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = async () => {
    if (currentStep > 1) {
      try { await autosaveFlush(); } catch (_) { /* non-fatal */ }
      setCurrentStep(prev => prev - 1);
    }
  };

  const getStepValidation = (step) => {
    switch (step) {
      case 1:
        return formData.job_title && formData.company_name;
      case 2:
        return formData.full_name && formData.email;
      case 3:
        return formData.content.length > 50;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Briefcase className="h-5 w-5 mr-2 text-blue-600" />
                Job Details
              </CardTitle>
              <CardDescription>
                Tell us about the position you're applying for
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="job_title">Job Title *</Label>
                <Input
                  id="job_title"
                  data-testid="cl-input-job-title"
                  value={formData.job_title}
                  onChange={(e) => handleInputChange('job_title', e.target.value)}
                  placeholder="e.g., Senior Software Engineer"
                  className="border-blue-200 focus:border-blue-500"
                />
                <FieldLengthHint field="job_title" value={formData.job_title} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  data-testid="company-name-input"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="e.g., Google Inc."
                  className="border-blue-200 focus:border-blue-500"
                />
                <FieldLengthHint field="company_name" value={formData.company_name} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="recipient_name">Recipient Name</Label>
                <Input
                  id="recipient_name"
                  data-testid="recipient-name-input"
                  value={formData.recipient_name}
                  onChange={(e) => handleInputChange('recipient_name', e.target.value)}
                  placeholder="e.g., Mr. John Smith (Hiring Manager)"
                  className="border-blue-200 focus:border-blue-500"
                />
                <FieldLengthHint field="recipient_name" value={formData.recipient_name} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="job_description">Job Description</Label>
                <Textarea
                  id="job_description"
                  value={formData.job_description}
                  onChange={(e) => handleInputChange('job_description', e.target.value)}
                  placeholder="Paste the job description here to help personalize your cover letter..."
                  rows={6}
                  className="border-blue-200 focus:border-blue-500"
                />
              </div>
            </CardContent>
          </Card>
        );

      case 2:
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <User className="h-5 w-5 mr-2 text-blue-600" />
                Personal Information
              </CardTitle>
              <CardDescription>
                Your contact information (auto-populated from resume)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    data-testid="cl-input-full-name"
                    value={formData.full_name}
                    onChange={(e) => handleInputChange('full_name', e.target.value)}
                    placeholder="Your full name"
                    className="border-blue-200 focus:border-blue-500"
                  />
                  <FieldLengthHint field="full_name" value={formData.full_name} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-1">
                    Email Address *
                    <Lock className="h-3 w-3 text-slate-400" />
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    readOnly
                    disabled
                    placeholder="your.email@example.com"
                    className="border-blue-200 bg-slate-50 cursor-not-allowed"
                    data-testid="cl-input-email"
                  />
                  <p className="text-xs text-slate-500">
                    Managed in your <strong>Profile</strong>.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="flex items-center gap-1">
                    Phone Number
                    <Lock className="h-3 w-3 text-slate-400" />
                  </Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    readOnly
                    disabled
                    placeholder="+1 (555) 123-4567"
                    className="border-blue-200 bg-slate-50 cursor-not-allowed"
                    data-testid="cl-input-phone"
                  />
                  <p className="text-xs text-slate-500">
                    Managed in your <strong>Profile</strong>.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="City, State/Country"
                    className="border-blue-200 focus:border-blue-500"
                  />
                  <FieldLengthHint field="location" value={formData.location} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="linkedin">LinkedIn Profile</Label>
                  <Input
                    id="linkedin"
                    value={formData.linkedin}
                    onChange={(e) => handleInputChange('linkedin', e.target.value)}
                    placeholder="https://linkedin.com/in/yourname"
                    className="border-blue-200 focus:border-blue-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Personal Website</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://yourname.com"
                    className="border-blue-200 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Key Strengths for PDF */}
              <div className="mt-6 pt-4 border-t border-blue-100">
                <Label className="text-sm font-semibold text-gray-700 mb-3 block">
                  Key Strengths (shown on PDF cover letter)
                </Label>
                <div className="space-y-4">
                  {(formData.key_strengths || []).map((strength, idx) => (
                    <div key={idx} className="space-y-1">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-start">
                        <div>
                          <Input
                            data-testid={`strength-title-${idx}`}
                            value={strength.title || ""}
                            onChange={(e) => handleStrengthChange(idx, 'title', e.target.value)}
                            placeholder={`Strength ${idx + 1} title`}
                            className="border-blue-200 focus:border-blue-500 text-sm"
                          />
                          <FieldLengthHint field="key_strength_title" value={strength.title || ""} />
                        </div>
                        <div className="md:col-span-2">
                          <Input
                            data-testid={`strength-desc-${idx}`}
                            value={strength.description || ""}
                            onChange={(e) => handleStrengthChange(idx, 'description', e.target.value)}
                            placeholder="Brief description"
                            className="border-blue-200 focus:border-blue-500 text-sm"
                          />
                          <FieldLengthHint field="key_strength_description" value={strength.description || ""} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 3:
        return (
          <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <PenTool className="h-5 w-5 mr-2 text-blue-600" />
                Content & Tone
              </CardTitle>
              <CardDescription>
                Write your cover letter and select the tone
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Cover Letter Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="e.g., Cover Letter for Senior Software Engineer"
                  className="border-blue-200 focus:border-blue-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tone">Tone</Label>
                <Select value={formData.tone} onValueChange={(value) => handleInputChange('tone', value)}>
                  <SelectTrigger className="border-blue-200 focus:border-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {toneOptions.map(tone => (
                      <SelectItem key={tone} value={tone}>{tone}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Cover Letter Content *</Label>
                <Textarea
                  id="content"
                  data-testid="cl-input-content"
                  value={formData.content}
                  onChange={(e) => handleInputChange('content', e.target.value)}
                  placeholder="Write your cover letter here..."
                  rows={12}
                  className="border-blue-200 focus:border-blue-500"
                />
                <div className="flex flex-col gap-1">
                  <p className="text-sm text-gray-500">
                    {formData.content.length} characters (minimum 50 recommended)
                  </p>
                  <FieldLengthHint field="content" value={formData.content} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Iter-28: Signature widget at the final review step.
              Profile remains the canonical store — this just gives
              the candidate a convenient in-context control while
              finalising the cover letter. */}
          <Card data-testid="cl-signature-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center text-base">
                <PenLine className="h-5 w-5 mr-2 text-blue-600" />
                Your signature
                <Lock className="h-3 w-3 ml-2 text-slate-400" aria-label="Synced from Profile" />
              </CardTitle>
              <CardDescription>
                Stored on your Profile and placed automatically on every cover letter. Add, change, or remove it here without leaving the review step.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div
                  className="h-20 w-40 sm:w-52 border rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0"
                  data-testid="cl-signature-preview"
                >
                  {signatureUrl ? (
                    <img
                      src={resolveAssetUrl(signatureUrl)}
                      alt="Signature"
                      className="max-h-16 max-w-48 object-contain"
                    />
                  ) : (
                    <span className="text-xs text-slate-400 text-center px-3">
                      No signature on file yet
                    </span>
                  )}
                </div>
                <div className="flex-1">
                  <Button
                    variant={signatureUrl ? "outline" : "default"}
                    size="sm"
                    onClick={() => setSignatureModalOpen(true)}
                    className={signatureUrl ? "" : "bg-blue-600 hover:bg-blue-700"}
                    data-testid="cl-signature-trigger"
                  >
                    <PenLine className="h-4 w-4 mr-2" />
                    {signatureUrl ? "Change signature" : "Add signature"}
                  </Button>
                  <p className="text-xs text-gray-500 mt-1.5">
                    Draw, upload, or remove. The preview updates as soon as you save.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          </>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading cover letter...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Cover Letter Generator</h1>
          <p className="text-gray-600 mt-1">Create personalized cover letters for your job applications</p>
        </div>
        <div className="flex items-center space-x-3">
          <AutosaveIndicator status={autosaveStatus} lastSavedAt={autosaveAt} />
          <Button onClick={handleSave} disabled={saving} variant="outline" data-testid="cover-letter-save-btn">
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save
          </Button>
          <Button onClick={handleDownload} variant="outline" disabled={!formData.content || saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Download PDF
          </Button>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          {steps.map((step) => (
            <div key={step.number} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                currentStep >= step.number 
                  ? 'bg-blue-600 border-blue-600 text-white' 
                  : 'border-gray-300 text-gray-500'
              }`}>
                {getStepValidation(step.number) ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  <step.icon className="h-5 w-5" />
                )}
              </div>
              <div className="ml-3 hidden md:block">
                <p className={`text-sm font-medium ${
                  currentStep >= step.number ? 'text-blue-600' : 'text-gray-500'
                }`}>
                  {step.title}
                </p>
                <p className="text-xs text-gray-500">{step.description}</p>
              </div>
              {step.number < 3 && (
                <div className={`w-16 h-1 ml-4 ${
                  currentStep > step.number ? 'bg-blue-600' : 'bg-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>
        <Progress value={(currentStep / 3) * 100} className="h-2" />
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Form */}
        <div className="space-y-6">
          {renderStepContent()}
          
          {/* Navigation */}
          <div className="flex justify-between">
            <Button 
              onClick={prevStep} 
              disabled={currentStep === 1}
              variant="outline"
              data-testid="cover-letter-back-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            {currentStep === 3 ? (
              // Iter-23.6: replace the ghosted/disabled Next button on the
              // final step with the meaningful primary CTA — Download PDF.
              // Iter-24: pair it with the combined Application Package
              // (cover letter + resume) export.
              // Iter-25: subtle print-in-color hint surfaced near the
              // Application Package CTA (kept out of the PDF itself).
              <div className="flex flex-col items-end gap-2">
                {/* Iter-37.6 — Optional Success Agreement add-on toggle.
                    Only shown when the candidate has an active agreement
                    available. Off by default — the standard package is
                    Cover Letter + Resume + QR-linked intro. */}
                {hasActiveAgreement && (
                  <label
                    className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none"
                    data-testid="package-include-agreement-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={includeSuccessAgreement}
                      onChange={(e) => setIncludeSuccessAgreement(e.target.checked)}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      data-testid="package-include-agreement-checkbox"
                    />
                    <span>
                      Include my <span className="font-semibold">Success Agreement</span> (optional add-on)
                    </span>
                  </label>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleDownload}
                    disabled={!formData.content || saving}
                    variant="outline"
                    data-testid="cover-letter-download-btn"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Cover Letter PDF
                  </Button>
                  <Button
                    onClick={handleDownloadPackage}
                    disabled={!formData.content || saving}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="cover-letter-download-package-btn"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Application Package
                  </Button>
                </div>
                <p
                  className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"
                  data-testid="print-color-hint"
                >
                  <Printer className="h-3.5 w-3.5" aria-hidden="true" />
                  Best results: print in color to preserve branding &amp; QR visibility.
                </p>
              </div>
            ) : (
              <Button 
                onClick={nextStep} 
                disabled={!getStepValidation(currentStep)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="cover-letter-next-btn"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </div>

        {/* Live PDF Preview */}
        <div>
          <Card className="h-fit sticky top-4" data-testid="pdf-preview-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Eye className="h-4 w-4 text-blue-600" />
                  Live PDF Preview
                </span>
                <div className="flex items-center gap-2">
                  {previewLoading && (
                    <Badge variant="outline" className="text-xs animate-pulse">
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Updating...
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={generatePreview}
                    disabled={previewLoading}
                    data-testid="refresh-preview-btn"
                    className="h-8 w-8 p-0"
                  >
                    <RefreshCw className={`h-4 w-4 ${previewLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </CardTitle>
              <CardDescription>
                See the actual PDF as you type — updates automatically
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pdfBlobUrl ? (
                <div className="space-y-3" data-testid="pdf-preview-container">
                  <div
                    className="relative bg-gray-100 rounded-lg overflow-hidden border border-gray-200"
                    style={{ height: "620px" }}
                  >
                    {previewLoading && (
                      <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                      </div>
                    )}
                    <iframe
                      src={`${pdfBlobUrl}#toolbar=0&navpanes=0&view=FitH`}
                      title="Cover Letter PDF Preview"
                      className="w-full h-full"
                      data-testid="pdf-preview-iframe"
                      style={{ border: "none" }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 text-center">
                    This is the actual PDF that will be downloaded
                  </p>
                </div>
              ) : previewError ? (
                <div className="text-center py-12" data-testid="pdf-preview-error">
                  <AlertCircle className="h-12 w-12 text-red-300 mx-auto mb-3" />
                  <p className="text-sm text-red-500 mb-3">{previewError}</p>
                  <Button variant="outline" size="sm" onClick={generatePreview}>
                    <RefreshCw className="h-3 w-3 mr-2" />
                    Retry
                  </Button>
                </div>
              ) : (
                <div className="text-center py-12" data-testid="pdf-preview-empty">
                  <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">PDF Preview</h3>
                  <p className="text-gray-500 mb-4">
                    Fill in your name, company, or content to see a live preview of your cover letter PDF
                  </p>
                  <div className="bg-blue-50 rounded-lg p-4 text-left">
                    <h4 className="font-medium text-blue-800 mb-2">Cover Letter Tips:</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>- Address the hiring manager by name if possible</li>
                      <li>- Mention specific achievements and quantify results</li>
                      <li>- Show enthusiasm for the company and role</li>
                      <li>- Keep it concise — aim for 3-4 paragraphs</li>
                      <li>- End with a strong call to action</li>
                    </ul>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Iter-36: Past Application Packages (locked QR re-print) ── */}
      <Card data-testid="past-packages-card" className="mt-2">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lock className="h-4 w-4 text-blue-600" />
            Past Application Packages
          </CardTitle>
          <CardDescription>
            Every download creates a new locked package. <span className="font-semibold">Re-print</span> a package
            below to get an identical PDF — same QR — for extra hard copies.
            Re-printing <span className="italic">never</span> creates a new QR.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {packagesLoading ? (
            <div className="flex items-center text-slate-500 text-sm py-4">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading…
            </div>
          ) : packages.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-5 text-sm text-slate-500 text-center" data-testid="past-packages-empty">
              No packages yet. Click <span className="font-semibold">Application Package</span> above to create your first locked package.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="past-packages-list">
              {packages.map((pkg) => {
                const created = pkg.created_at ? new Date(pkg.created_at) : null;
                const dateStr = created
                  ? created.toLocaleString(undefined, {
                      month: "short", day: "numeric", year: "numeric",
                      hour: "numeric", minute: "2-digit",
                    })
                  : "—";
                const mode = pkg.intro_mode_at_print || "hidden";
                const modeBadge =
                  mode === "video" ? { label: "Video intro", cls: "bg-emerald-100 text-emerald-800" }
                    : mode === "audio" ? { label: "Audio intro", cls: "bg-blue-100 text-blue-800" }
                      : { label: "No intro (hidden)", cls: "bg-slate-100 text-slate-700" };
                const publicUrl = `/video/${user?.id || ""}/p/${pkg.id}`;
                return (
                  <li
                    key={pkg.id}
                    className="py-3 flex flex-wrap items-center gap-3 justify-between"
                    data-testid={`past-package-row-${pkg.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold text-slate-800 truncate" data-testid={`past-package-title-${pkg.id}`}>
                          {pkg.cover_letter_title || "Application Package"}
                        </span>
                        <Badge className={`${modeBadge.cls} text-[10px] uppercase tracking-wider`}>
                          {modeBadge.label}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 font-mono truncate">
                        {dateStr} · package <span className="text-slate-700">{pkg.id.slice(0, 8)}</span>
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <a
                        href={publicUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-xs text-blue-700 hover:text-blue-900 hover:underline"
                        data-testid={`past-package-open-link-${pkg.id}`}
                      >
                        <Eye className="h-3.5 w-3.5 mr-1" />
                        Open locked URL
                      </a>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={reprintingId === pkg.id || !pkg.package_pdf_url}
                        onClick={() => handleReprintPackage(pkg)}
                        data-testid={`past-package-reprint-btn-${pkg.id}`}
                      >
                        {reprintingId === pkg.id ? (
                          <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        ) : (
                          <Printer className="h-4 w-4 mr-1.5" />
                        )}
                        Re-print
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <ProfileSyncModal
        open={syncModalOpen}
        onClose={handleSyncCancel}
        onChooseDocumentOnly={handleSyncDocOnly}
        onChooseSyncAll={handleSyncAll}
        documentLabel="Cover Letter"
        changedFields={pendingChangedFields}
        busy={saving}
      />

      <SignatureModal
        open={signatureModalOpen}
        onOpenChange={setSignatureModalOpen}
        hasSignature={Boolean(signatureUrl)}
        onChange={(url) => {
          // Profile is the canonical store — refresh from it after
          // any save so the URL we render matches what the PDF will
          // pull at render time.
          setSignatureUrl(url || "");
          // Trigger a fresh PDF preview so the embedded signature
          // updates immediately in the side-by-side iframe.
          generatePreview();
        }}
      />
    </div>
  );
};

export default CoverLetterGenerator;